import { LocalStorageTokenProvider } from '@/utils/access-token-provider';

import { api } from '@/api';
import { AuthStore } from './auth';
import {
  CachedStorage,
  CollectionStore,
  DummyDataLoader,
  SimpleStoreStorage,
  StoreDataLoader,
  RelStoreDataLoader,
  RelCollectionStore,
} from './collection';
import { LocaleStore } from './locale';
import { SettingsStore } from './settings';
import { DashboardGlobalStore } from './dashboard-global';

export const authStore = new AuthStore(new LocalStorageTokenProvider());
export const localeStore = new LocaleStore();
export const settingsStore = new SettingsStore();

const getDefaultListParams = () => ({ limit: settingsStore.itemsPerPage });
const createListLoader = <P extends object>(fn: (params: P) => any) =>
  new StoreDataLoader((params?: P) => fn({ ...getDefaultListParams(), ...(params ?? {}) } as P));
const createRelListLoader = <P extends object>(fn: (id: number, params: P) => any) =>
  new RelStoreDataLoader((id: number, params?: P) => fn(id, { ...getDefaultListParams(), ...(params ?? {}) } as P));

export const lotStore = new CollectionStore<cyrian.api.lots.Lot, CommonGetParams>(
  createListLoader(api.lots.getAll),
  new CachedStorage(),
);
export const lotAnalysisStore = new RelCollectionStore<cyrian.api.lots.LotAnalysis, CommonGetParams>(
  createRelListLoader(api.lots.getAnalyses),
  new CachedStorage(),
);
export const lotWeighingStore = new RelCollectionStore<cyrian.api.lots.LotWeighing, CommonGetParams>(
  createRelListLoader(api.lots.getWeighings),
  new CachedStorage(),
);
export const lotFumigationStore = new RelCollectionStore<cyrian.api.lots.LotFumigation, CommonGetParams>(
  createRelListLoader(api.lots.getFumigations),
  new CachedStorage(),
);
export const lotPledgeStore = new RelCollectionStore<cyrian.api.lots.LotPledge, CommonGetParams>(
  createRelListLoader(api.lots.getPledges),
  new CachedStorage(),
);
export const lotWaybillStore = new RelCollectionStore<cyrian.api.waybills.Waybill, cyrian.api.waybills.GetParams>(
  createRelListLoader(api.lots.getWaybills),
  new CachedStorage(),
);

export const waybillStore = new CollectionStore<cyrian.api.waybills.Waybill, cyrian.api.waybills.GetParams>(
  createListLoader(api.waybills.getAll),
  new CachedStorage(),
);
export const waybillWeighingAnalysisStore = new RelCollectionStore<
  cyrian.api.waybillsWeighingAnalyses.WaybillWeighingAnalysis,
  cyrian.api.waybillsWeighingAnalyses.GetParams
>(createRelListLoader(api.waybills.getWeighingAnalyses), new CachedStorage());

export const bvStore = new CollectionStore<cyrian.api.bvs.BV, cyrian.api.bvs.GetParams>(
  createListLoader(api.bvs.getAll),
  new CachedStorage(),
);
export const cvStore = new CollectionStore<cyrian.api.cvs.CV, cyrian.api.cvs.GetParams>(
  createListLoader(api.cvs.getAll),
  new CachedStorage(),
);
export const cvExecutionStore = new CollectionStore<
  cyrian.api.cvExecutions.CVExecution,
  cyrian.api.cvExecutions.GetParams
>(createListLoader(api.cvExecutions.getAll), new CachedStorage());
export const campaignStore = new CollectionStore<cyrian.api.campaigns.Campaign, cyrian.api.campaigns.GetParams>(
  createListLoader(api.campaigns.getAll),
  new CachedStorage(),
);
export const consortiumStore = new CollectionStore<cyrian.api.consortiums.Consortium, cyrian.api.consortiums.GetParams>(
  createListLoader(api.consortiums.getAll),
  new CachedStorage(),
);
export const cooperativeStore = new CollectionStore<
  cyrian.api.cooperatives.Cooperative,
  cyrian.api.cooperatives.GetParams
>(createListLoader(api.cooperatives.getAll), new CachedStorage());
export const counterpartyStore = new CollectionStore<
  cyrian.api.counterparties.Counterparty,
  cyrian.api.counterparties.GetParams
>(createListLoader(api.counterparties.getAll), new CachedStorage());
export const currencyStore = new CollectionStore<cyrian.api.currencies.Currency, cyrian.api.currencies.GetParams>(
  createListLoader(api.currencies.getAll),
  new CachedStorage(),
);
export const productStore = new CollectionStore<cyrian.api.products.Product, cyrian.api.products.GetParams>(
  createListLoader(api.products.getAll),
  new CachedStorage(),
);
export const addressStore = new CollectionStore<cyrian.api.addresses.Address, cyrian.api.addresses.GetParams>(
  createListLoader(api.addresses.getAll),
  new CachedStorage(),
);
export const counterpartyContractStore = new CollectionStore<
  cyrian.api.counterpartyContracts.CounterpartyContract,
  cyrian.api.counterpartyContracts.GetParams
>(createListLoader(api.counterpartyContracts.getAll), new CachedStorage());
export const lotEventStore = new CollectionStore<cyrian.api.lots.Event, cyrian.api.lots.GetEventParams>(
  new DummyDataLoader(),
  new SimpleStoreStorage(),
);
export const boardingStore = new CollectionStore<cyrian.api.boardings.Boarding, cyrian.api.boardings.GetParams>(
  createListLoader(api.boardings.getAll),
  new CachedStorage(),
);
export const transitOrderStore = new CollectionStore<
  cyrian.api.transitOrders.TransitOrder,
  cyrian.api.transitOrders.GetParams
>(createListLoader(api.transitOrders.getAll), new CachedStorage());
export const containerStore = new CollectionStore<cyrian.api.containers.Container, cyrian.api.containers.GetParams>(
  createListLoader(api.containers.getAll),
  new CachedStorage(),
);
export const transferStore = new CollectionStore<cyrian.api.transfers.Transfer, cyrian.api.containers.GetParams>(
  createListLoader(api.transfers.getAll),
  new CachedStorage(),
);
export const contractLineItemStore = new CollectionStore<
  cyrian.api.contractLineItems.LineItem,
  cyrian.api.containers.GetParams
>(createListLoader(api.contractLineItems.getAll), new CachedStorage());
export const formulaStore = new CollectionStore<cyrian.api.formulas.Formula, cyrian.api.formulas.GetParams>(
  createListLoader(api.formulas.getAll),
  new CachedStorage(),
);

export const farmerStore = new CollectionStore<cyrian.api.farmers.Farmer, cyrian.api.farmers.GetParams>(
  createListLoader(api.farmers.getAll),
  new CachedStorage(),
);
export const farmStore = new CollectionStore<cyrian.api.farms.Farm, cyrian.api.farms.GetParams>(
  createListLoader(api.farms.getAll),
  new CachedStorage(),
);
export const farmerPlotsStore = new RelCollectionStore<cyrian.api.farmPlots.FarmPlot, cyrian.api.farmPlots.GetParams>(
  createRelListLoader(api.farmers.getPlots),
  new CachedStorage(),
);

export const attachmentHistoryStore = new CollectionStore<
  cyrian.api.attachments.Attachment,
  cyrian.api.attachments.HistoryGetParams
>(createListLoader(api.attachments.getHistory), new SimpleStoreStorage());

// Bulk PDF import jobs (Waybills → Imports page). SimpleStoreStorage rather
// than CachedStorage because the page polls actively and we always want the
// freshest list rather than a cached one.
export const documentImportStore = new CollectionStore<
  cyrian.api.documentImports.DocumentImport,
  cyrian.api.documentImports.GetParams
>(createListLoader(api.documentImports.getAll), new SimpleStoreStorage());

// Shipping-instruction imports (Boarding imports page). Same endpoint as
// documentImportStore but a separate instance — the two pages poll with
// different sticky `type` filters and must not clobber each other's items.
export const boardingImportStore = new CollectionStore<
  cyrian.api.documentImports.DocumentImport<cyrian.api.documentImports.ShippingInstructionParsed>,
  cyrian.api.documentImports.GetParams
>(createListLoader(api.documentImports.getAll), new SimpleStoreStorage());

export const dashboardGlobalStore = new DashboardGlobalStore();
